import { useFormikWithDirtyForm } from "@shared";
import { useAlertContext } from "@shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ClientDetailResponse, clientsApi } from "@/api";
import { QueryKey } from "@/query/Query";
import { useTranslation } from "@/translations";

import { ClientOverviewFormValues } from "../OverviewTab.types";

export function useClientOverview(client: ClientDetailResponse) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { showSuccess } = useAlertContext();

  const { data: clientOverview } = useQuery({
    queryKey: [QueryKey.ClientOverview, client.clientId],
    queryFn: () => clientsApi.getClientOverview({ clientId: client.clientId }),
    staleTime: Infinity,
  });

  const updateOverviewMutation = useMutation({
    mutationFn: (values: ClientOverviewFormValues) =>
      clientsApi.updateClientOverview({
        clientId: client.clientId,
        updateClientOverviewRequest: {
          estimatedDebt: values.estimatedDebt ?? undefined,
          estimatedNumberOfCreditors: values.estimatedNumberOfCreditors ?? undefined,
        },
      }),
    onSuccess: (saved) => {
      showSuccess(t("clients.client.overview.saveSuccess"));
      queryClient.setQueryData([QueryKey.ClientOverview, client.clientId], saved);
    },
  });

  const formik = useFormikWithDirtyForm<ClientOverviewFormValues>({
    enableReinitialize: true,
    initialValues: {
      estimatedDebt: clientOverview?.estimatedDebt ?? null,
      estimatedNumberOfCreditors: clientOverview?.estimatedNumberOfCreditors ?? null,
    },
    onSubmit: async (values) => {
      await updateOverviewMutation.mutateAsync(values);
    },
  });

  return { formik, lastAgreement: clientOverview?.lastAgreement ?? null };
}
